import { Formik, Form, Field } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { BsInstagram, BsYoutube } from "react-icons/bs";
import { FiLink, FiLock } from "react-icons/fi";
import * as C from "./styled";
import * as S from "../SignUp/styles";
import * as Common from "../../common-styles";
import ColoredLogo from "../../Assets/Images/logo-colored.svg";
import * as I from "../../Config/interface.config";
import * as Path from "../../API/path";
import APIRequest from "../../API";
import { AppDispatch, RootState } from "../../Redux/store";
import { logout } from "../../Redux/slices/authSlice";

const requirements = [
  "10,000+ followers & 18+",
  "Keep 80% of your earnings",
  "Verification & bank account required for payouts",
  "Follow content guidelines; agencies can reach at business@fanxo.club",
];

const INDIA_CODE = "+91";
const INTERNATIONAL_CODE = "INTL";

const normalizeDigits = (value: string) => value.replace(/\D/g, "");

const CreatorDetails = () => {
  const { loading } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const validationSchema = Yup.object().shape({
    username: Yup.string()
      .matches(/^\S*$/, "Username must be without spaces")
      .required("Username is required"),
    countryCode: Yup.string().oneOf([INDIA_CODE, INTERNATIONAL_CODE]).required("Contact type is required"),
    contactNumber: Yup.string().when("countryCode", {
      is: INDIA_CODE,
      then: (schema) =>
        schema
          .test("india-mobile", "Enter a valid 10 digit Indian mobile number", (value) => {
            const digits = normalizeDigits(value || "");
            return /^[6-9]\d{9}$/.test(digits);
          })
          .required("Contact number is required"),
      otherwise: (schema) => schema.trim().required("Contact number is required"),
    }),
    instagram: Yup.string(),
    youtube: Yup.string(),
    others: Yup.string(),
  });

  const initialValues: I.CreatorDetails = {
    username: "",
    countryCode: INDIA_CODE,
    contactNumber: "",
    instagram: "",
    youtube: "",
    others: "",
  };

  const handleSubmit = (values: I.CreatorDetails, { setSubmitting }: any) => {
    const contactNumber =
      values.countryCode === INDIA_CODE
        ? `91${normalizeDigits(values.contactNumber)}`
        : values.contactNumber.trim();
    const promise = APIRequest(Path.updateUser, {
      socialLinks: {
        instagram: values.instagram,
        youtube: values.youtube,
        others: values.others,
      },
      username: values.username,
      contactNumber,
    });

    toast.promise(promise, {
      loading: "updating details",

      success: (data: any) => {
        setSubmitting(false);

        if (data?.error) throw new Error(data?.error?.message);
        localStorage.clear();
        dispatch(logout());
        navigate("/thankyou-for-applying");
        return "Details stored successfully";
      },

      error: (err: any) => {
        setSubmitting(false);
        return err.message;
      },
    });
  };

  return (
    <S.SignUpWrapper style={{ alignItems: "flex-start" }}>
      <C.CreatorDetailsGlobalStyle />
      <S.SignUpContainer>
        <S.ColoredLogo src={ColoredLogo} alt="Fanxo" />
        <S.SignUpContent className="creator-details-content">
          <h1 className="font-roboto font-reg capitalize">Creator Application</h1>

          <div className="login-form">
            <C.RequirementsBlock>
              <C.RequirementsHeading>Fanxo Requires:</C.RequirementsHeading>
              <C.RequirementsList>
                {requirements.map((requirement) => (
                  <li key={requirement}>{requirement}</li>
                ))}
              </C.RequirementsList>
            </C.RequirementsBlock>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, errors, touched, isValid, dirty, values, setFieldValue }) => (
                <Form>
                  <div className="login-form">
                    <C.FieldGroup>
                      <C.SectionLabel>Enter your desired username <span>on fanxo</span></C.SectionLabel>
                      <C.InputRow>
                        <C.StaticPrefix>@</C.StaticPrefix>
                        <Field type="text" name="username" placeholder="Username" as={Common.CommonInput} />
                      </C.InputRow>
                      <C.ErrorText>{errors.username && touched.username && errors.username}</C.ErrorText>
                    </C.FieldGroup>

                    <C.FieldGroup>
                      <C.SectionLabel>Enter your contact details</C.SectionLabel>
                      <C.ContactRow>
                        <C.ContactSelector
                          name="countryCode"
                          aria-label="Contact number type"
                          value={values.countryCode}
                          onChange={(event) => {
                            setFieldValue("countryCode", event.target.value);
                            setFieldValue("contactNumber", "");
                          }}
                        >
                          <option value={INDIA_CODE}>+91</option>
                          <option value={INTERNATIONAL_CODE}>INTL</option>
                        </C.ContactSelector>
                        <Field
                          type="tel"
                          name="contactNumber"
                          inputMode={values.countryCode === INDIA_CODE ? "numeric" : undefined}
                          pattern={values.countryCode === INDIA_CODE ? "[0-9]*" : undefined}
                          maxLength={values.countryCode === INDIA_CODE ? 10 : undefined}
                          placeholder={values.countryCode === INDIA_CODE ? "10 digit mobile number" : "Full number with country code"}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const nextValue =
                              values.countryCode === INDIA_CODE
                                ? normalizeDigits(event.target.value).slice(0, 10)
                                : event.target.value;
                            setFieldValue("contactNumber", nextValue);
                          }}
                          as={Common.CommonInput}
                        />
                      </C.ContactRow>
                      <C.ErrorText>
                        {(errors.countryCode && touched.countryCode && errors.countryCode) ||
                          (errors.contactNumber && touched.contactNumber && errors.contactNumber)}
                      </C.ErrorText>
                    </C.FieldGroup>

                    <C.FieldGroup>
                      <C.SectionLabel>Enter your social username <span>or profile links</span></C.SectionLabel>
                      <C.InputRow>
                        <BsInstagram aria-hidden="true" />
                        <Field type="text" name="instagram" placeholder="Instagram" as={Common.CommonInput} />
                      </C.InputRow>
                      <C.ErrorText>{errors.instagram && touched.instagram && errors.instagram}</C.ErrorText>

                      <C.InputRow>
                        <BsYoutube aria-hidden="true" />
                        <Field type="text" name="youtube" placeholder="YouTube" as={Common.CommonInput} />
                      </C.InputRow>
                      <C.ErrorText>{errors.youtube && touched.youtube && errors.youtube}</C.ErrorText>

                      <C.InputRow>
                        <FiLink aria-hidden="true" />
                        <Field type="text" name="others" placeholder="Others" as={Common.CommonInput} />
                      </C.InputRow>
                      <C.ErrorText>{errors.others && touched.others && errors.others}</C.ErrorText>
                    </C.FieldGroup>

                    <C.CreatorContinueButton
                      variant="primary"
                      $isActive={dirty && isValid}
                      type="submit"
                      disabled={isSubmitting || loading || !(dirty && isValid)}
                    >
                      {isSubmitting ? "Submitting..." : "Continue"}
                    </C.CreatorContinueButton>
                  </div>
                </Form>
              )}
            </Formik>

            <C.SecurityNote>
              <FiLock aria-hidden="true" />
              <span>Your information is encrypted and used only for verification.</span>
            </C.SecurityNote>
          </div>
        </S.SignUpContent>
      </S.SignUpContainer>
    </S.SignUpWrapper>
  );
};

export default CreatorDetails;
